import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Waveform, FileText } from "@phosphor-icons/react";
import GoogleAuthButton from "@/components/GoogleAuthButton";

const Signup = () => {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "artist" ? "artist" : "fan";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    role: initialRole,
    stage_name: "",
    rib: "",
    accept_contract: false,
  });
  const [loading, setLoading] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [contract, setContract] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Load contract once for artist flow
  useEffect(() => {
    if (form.role === "artist" && !contract) {
      api.get("/legal/artist-contract").then((r) => setContract(r.data)).catch(() => {});
    }
  }, [form.role, contract]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "artist" && !form.accept_contract) {
      setContractOpen(true);
      return;
    }
    setLoading(true);
    try {
      const u = await signup(form);
      toast.success(`Bienvenue ${u.full_name} !`);
      nav(u.role === "artist" ? "/dashboard" : "/concerts");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Inscription impossible");
    } finally {
      setLoading(false);
    }
  };

  const signContractAndSubmit = async () => {
    update("accept_contract", true);
    setContractOpen(false);
    setLoading(true);
    try {
      const u = await signup({ ...form, accept_contract: true });
      toast.success(`Contrat signé. Bienvenue ${u.full_name} !`);
      nav(u.role === "artist" ? "/dashboard" : "/concerts");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Inscription impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <Waveform weight="fill" size={28} className="text-[#E11D48]" />
          <span className="font-display font-black text-2xl">XooKmédia</span>
        </Link>

        <div className="bg-[#0f0f10] border border-white/10 rounded-2xl p-8">
          <h1 className="font-display font-black text-3xl mb-2">Créez votre scène</h1>
          <p className="text-zinc-400 text-sm mb-8">Gratuit pour les artistes. Toujours.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-[0.2em] mb-3 block">Je suis</Label>
              <RadioGroup
                value={form.role}
                onValueChange={(v) => update("role", v)}
                className="grid grid-cols-2 gap-3"
              >
                <label
                  data-testid="signup-role-fan"
                  className={`cursor-pointer p-4 rounded-lg border ${form.role === "fan" ? "border-[#E11D48] bg-[#E11D48]/10" : "border-white/10 bg-black/30"} flex items-center gap-3`}
                >
                  <RadioGroupItem value="fan" id="r-fan" />
                  <span className="font-display font-bold">Fan</span>
                </label>
                <label
                  data-testid="signup-role-artist"
                  className={`cursor-pointer p-4 rounded-lg border ${form.role === "artist" ? "border-[#E11D48] bg-[#E11D48]/10" : "border-white/10 bg-black/30"} flex items-center gap-3`}
                >
                  <RadioGroupItem value="artist" id="r-artist" />
                  <span className="font-display font-bold">Artiste</span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="full_name" className="text-zinc-300">Nom complet</Label>
              <Input data-testid="signup-fullname" id="full_name" required value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                className="mt-2 bg-black/40 border-white/10 text-white" />
            </div>
            {form.role === "artist" && (
              <>
                <div>
                  <Label htmlFor="stage_name" className="text-zinc-300">Nom de scène</Label>
                  <Input data-testid="signup-stagename" id="stage_name" required value={form.stage_name}
                    onChange={(e) => update("stage_name", e.target.value)}
                    className="mt-2 bg-black/40 border-white/10 text-white" />
                </div>
                <div>
                  <Label htmlFor="rib" className="text-zinc-300">RIB / IBAN (pour vos revenus)</Label>
                  <Input data-testid="signup-rib" id="rib" placeholder="FR76..." required value={form.rib}
                    onChange={(e) => update("rib", e.target.value)}
                    className="mt-2 bg-black/40 border-white/10 text-white font-mono text-sm" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input data-testid="signup-email" id="email" type="email" required value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-2 bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
              <Input data-testid="signup-password" id="password" type="password" required minLength={6}
                value={form.password} onChange={(e) => update("password", e.target.value)}
                className="mt-2 bg-black/40 border-white/10 text-white" />
            </div>

            {form.role === "artist" && (
              <button
                type="button"
                onClick={() => setContractOpen(true)}
                data-testid="signup-open-contract"
                className="w-full flex items-center gap-2 p-3 rounded-lg border border-white/10 bg-black/30 hover:border-[#E11D48]/50 text-left text-sm transition-colors"
              >
                <FileText size={18} className="text-[#E11D48]" />
                <div className="flex-1">
                  <div className="font-bold text-white">Contrat partenariat artiste</div>
                  <div className="text-xs text-zinc-500">
                    {form.accept_contract ? "✓ Signé électroniquement" : "À lire et signer"}
                  </div>
                </div>
              </button>
            )}

            <Button data-testid="signup-submit" type="submit" disabled={loading}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white mt-2">
              {loading ? "Création..." : form.role === "artist" && !form.accept_contract ? "Lire et signer le contrat" : "Créer mon compte"}
            </Button>
          </form>

          {form.role === "fan" && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs uppercase tracking-widest text-zinc-500">ou</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <GoogleAuthButton role="fan" label="S'inscrire avec Google" testId="signup-google" />
            </>
          )}

          <p className="mt-6 text-center text-sm text-zinc-400">
            Déjà inscrit ?{" "}
            <Link to="/login" className="text-[#E11D48] hover:text-[#F59E0B]" data-testid="signup-link-login">Se connecter</Link>
          </p>
        </div>
      </div>

      {/* Contract Dialog */}
      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent className="bg-[#0f0f10] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <FileText size={22} className="text-[#E11D48]" />
              Contrat partenariat artiste — v{contract?.version || "1.0"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Document confidentiel — réservé à l'artiste signataire.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh] pr-4 -mr-4">
            <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans leading-relaxed" data-testid="contract-text">
              {contract?.text || "Chargement du contrat..."}
            </pre>
          </ScrollArea>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-black/40 border border-white/5">
            <Checkbox
              data-testid="contract-accept-checkbox"
              id="accept"
              checked={form.accept_contract}
              onCheckedChange={(v) => update("accept_contract", !!v)}
              className="mt-0.5"
            />
            <label htmlFor="accept" className="text-sm text-zinc-300 cursor-pointer leading-snug">
              J'ai lu et j'accepte le contrat. Je reconnais qu'il sera <strong>auto-signé électroniquement</strong> et conservé dans mon espace artiste, horodaté à la date d'ouverture de mon compte.
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => setContractOpen(false)}>
              Fermer
            </Button>
            <Button
              data-testid="contract-sign-submit"
              disabled={!form.accept_contract || loading || !form.full_name || !form.email || !form.password || !form.stage_name || !form.rib}
              onClick={signContractAndSubmit}
              className="bg-[#E11D48] hover:bg-[#BE123C]"
            >
              Signer et créer mon compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Signup;
